import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useDisplayNotifications } from '../../contexts/DisplayNotificationContext';
import { DisplayData, fetchDisplays, activateDisplay, deactivateDisplay, deleteDisplay } from '../../services/displayService';
import StatusIndicator from '../../components/common/StatusIndicator';
import { useAuth } from '../../contexts/AuthContext';

const DisplaysListPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [displays, setDisplays] = useState<DisplayData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const { addNotification } = useDisplayNotifications();
  
  // Sort and filter states
  const [sortField, setSortField] = useState<'name' | 'status' | 'lastActive' | 'notesShown'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'pending'>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Check if user has paid subscription tier for display management
  const userSubscriptionTier = currentUser?.subscriptionTier || 'free';
  const hasPaidSubscription = ['individual', 'group', 'organization', 'custom'].includes(userSubscriptionTier);

  // Fetch all displays on component mount
  useEffect(() => {
    const loadDisplays = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchDisplays();
        setDisplays(data);
      } catch (err) {
        console.error('Error loading displays:', err);
        setError(err instanceof Error ? err.message : 'Failed to load displays');
      } finally {
        setLoading(false);
      }
    };

    // Only load displays if user has paid subscription
    if (hasPaidSubscription) {
      loadDisplays();
    } else {
      setLoading(false); // Don't show loading for non-paid users
    }
  }, [hasPaidSubscription]);

  // Auto-clear action messages after 5 seconds
  useEffect(() => {
    if (actionMessage) {
      const timer = setTimeout(() => {
        setActionMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [actionMessage]);

  // Handle display activation
  const handleActivateDisplay = async (displayId: string) => {
    try {
      const updatedDisplay = await activateDisplay(displayId);
      setDisplays(prevDisplays => 
        prevDisplays.map(display => 
          display._id === displayId ? updatedDisplay : display
        )
      );
      setActionMessage({ type: 'success', text: 'Display activated successfully!' });
      addNotification({
        type: 'success',
        message: 'Your display has been activated and is now showing notes.',
        displayId: displayId,
        displayName: updatedDisplay.name
      });
    } catch (err) {
      console.error('Error activating display:', err);
      setActionMessage({ 
        type: 'error', 
        text: err instanceof Error ? err.message : 'Failed to activate display' 
      });
    }
  };

  // Handle display deactivation
  const handleDeactivateDisplay = async (displayId: string) => {
    try {
      const updatedDisplay = await deactivateDisplay(displayId);
      setDisplays(prevDisplays => 
        prevDisplays.map(display => 
          display._id === displayId ? updatedDisplay : display
        )
      );
      setActionMessage({ type: 'success', text: 'Display deactivated successfully!' });
      addNotification({
        type: 'warning',
        message: 'Your display has been deactivated and is no longer showing notes.',
        displayId: displayId,
        displayName: updatedDisplay.name
      });
    } catch (err) {
      console.error('Error deactivating display:', err);
      setActionMessage({ 
        type: 'error', 
        text: err instanceof Error ? err.message : 'Failed to deactivate display' 
      });
    }
  };

  // Handle display deletion
  const handleDeleteDisplay = async (displayId: string, displayName: string) => {
    const confirmed = window.confirm(`Are you sure you want to delete the display "${displayName}"? This action cannot be undone.`);
    if (!confirmed) return;

    try {
      await deleteDisplay(displayId);
      setDisplays(prevDisplays => prevDisplays.filter(display => display._id !== displayId));
      setActionMessage({ type: 'success', text: 'Display deleted successfully!' });
      addNotification({
        type: 'info',
        message: `Display "${displayName}" has been permanently deleted.`,
        displayId: displayId,
        displayName: displayName
      });
    } catch (err) {
      console.error('Error deleting display:', err);
      setActionMessage({ 
        type: 'error', 
        text: err instanceof Error ? err.message : 'Failed to delete display' 
      });
    }
  };

  // Helper function to get last active timestamp for sorting
  const getLastActiveTimestamp = (display: DisplayData): number => {
    if (!display.analytics?.lastActiveDate) return 0;
    return new Date(display.analytics.lastActiveDate).getTime();
  };

  // Helper function to format last active time
  const formatLastActiveTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) {
      const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
      if (diffInHours === 0) {
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
      }
      return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
    } else if (diffInDays === 1) {
      return 'Yesterday';
    } else if (diffInDays < 7) {
      return `${diffInDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Filter and sort displays
  const filteredAndSortedDisplays = useMemo(() => {
    // Ensure displays is always an array to prevent iteration errors
    if (!displays || !Array.isArray(displays)) {
      return [];
    }
    
    // First filter by status
    let result = [...displays];
    
    if (statusFilter !== 'all') {
      result = result.filter(display => display.status === statusFilter);
    }
    
    // Then filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(display => 
        display.name.toLowerCase().includes(query) || 
        (display.customName?.toLowerCase().includes(query)) || 
        (display.location?.city?.toLowerCase().includes(query)) ||
        (display.location?.state?.toLowerCase().includes(query)) ||
        (display.location?.zipcode?.includes(query)) ||
        (display.location?.label?.toLowerCase().includes(query))
      );
    }
    
    // Then sort
    result.sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case 'name':
          comparison = (a.name || '').localeCompare(b.name || '');
          break;
        case 'status':
          comparison = (a.status || '').localeCompare(b.status || '');
          break;
        case 'lastActive':
          comparison = getLastActiveTimestamp(a) - getLastActiveTimestamp(b);
          break;
        case 'notesShown':
          comparison = (a.analytics?.totalNotesShown || 0) - (b.analytics?.totalNotesShown || 0);
          break;
        default:
          comparison = 0;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    
    return result;
  }, [displays, sortField, sortDirection, statusFilter, searchQuery]);

  // Get sort icon
  const getSortIcon = (field: typeof sortField) => {
    if (sortField !== field) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  // If user doesn't have paid subscription, show upgrade message
  if (!hasPaidSubscription) {
    return (
      <div className="container my-4">
        <div className="row justify-content-center">
          <div className="col-md-8">
            <div className="card">
              <div className="card-body text-center py-5">
                <i className="fas fa-desktop" style={{ fontSize: '4rem', color: '#6c757d' }}></i>
                <h2 className="mt-3 mb-3">Display Management</h2>
                <p className="lead text-muted mb-4">
                  Display management is a premium feature that allows you to set up digital displays 
                  to showcase inspiring notes in public spaces.
                </p>
                <div className="alert alert-info mb-4">
                  <h5 className="alert-heading">
                    <i className="fas fa-info-circle me-2"></i>
                    Upgrade Required
                  </h5>
                  <p className="mb-0">
                    Your current subscription ({userSubscriptionTier}) does not include display management. 
                    Please upgrade to <strong>Individual</strong> or higher to access this feature.
                  </p>
                </div>
                <div className="d-flex justify-content-center gap-3">
                  <Link to="/contributor/donations" className="btn btn-primary">
                    <i className="fas fa-arrow-circle-up me-2"></i>
                    Upgrade Subscription
                  </Link>
                  <Link to="/dashboard" className="btn btn-outline-secondary">
                    <i className="fas fa-arrow-left me-2"></i>
                    Back to Dashboard
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container my-4 text-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading displays...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container my-4">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container my-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>My Displays</h1>
      </div>

      {/* Action message display */}
      {actionMessage && (
        <div className={`alert alert-${actionMessage.type === 'success' ? 'success' : 'danger'} alert-dismissible`} role="alert">
          {actionMessage.text}
          <button type="button" className="btn-close" onClick={() => setActionMessage(null)}></button>
        </div>
      )}

      {displays.length === 0 ? (
        <div className="row justify-content-center">
          <div className="col-md-8">
            <div className="card">
              <div className="card-body text-center py-5">
                <i className="fas fa-desktop" style={{ fontSize: '4rem', color: '#6c757d' }}></i>
                <h2 className="mt-3 mb-3">No Displays Found</h2>
                <p className="lead text-muted mb-4">
                  You haven't registered any displays yet. Register a display to start showing community notes.
                </p>
                
                <div className="alert alert-info mb-4">
                  <h5 className="alert-heading">
                    <i className="fas fa-info-circle me-2"></i>
                    How to Register a Display
                  </h5>
                  <ul className="mb-0 text-start">
                    <li>Click the "Register Display" button below</li>
                    <li>Follow the setup instructions</li>
                    <li>Configure your display settings</li>
                    <li>Start displaying community notes!</li>
                  </ul>
                </div>
                
                <div className="d-flex justify-content-center gap-3">
                  <Link to="/displays/register" className="btn btn-primary">
                    <i className="fas fa-arrow-circle-up me-2"></i>
                    Register Display
                  </Link>
                  <Link to="/dashboard" className="btn btn-outline-secondary">
                    <i className="fas fa-arrow-left me-2"></i>
                    Back to Dashboard
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Filter and sort controls */}
          <div className="card mb-3">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-4">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search displays..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <select 
                    className="form-select"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as any)}
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <select 
                    className="form-select"
                    value={sortField}
                    onChange={(e) => setSortField(e.target.value as any)}
                  >
                    <option value="name">Sort by Name {getSortIcon('name')}</option>
                    <option value="status">Sort by Status {getSortIcon('status')}</option>
                    <option value="lastActive">Sort by Last Active {getSortIcon('lastActive')}</option>
                    <option value="notesShown">Sort by Notes Shown {getSortIcon('notesShown')}</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <button 
                    className="btn btn-outline-secondary w-100"
                    onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                  >
                    {sortDirection === 'asc' ? '↑ Asc' : '↓ Desc'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <span>Your Registered Displays ({filteredAndSortedDisplays.length} of {displays.length})</span>
              <small className="text-muted">
                <i className="fas fa-info-circle me-1"></i>
                To add more displays, visit <code>{window.location.origin}/display/view/</code> on your new device and scan the QR code
              </small>
            </div>
            <div className="list-group list-group-flush">
              {filteredAndSortedDisplays.length === 0 ? (
                <div className="list-group-item text-center py-4">
                  <p className="mb-0">No displays match your current filters</p>
                </div>
              ) : (
                filteredAndSortedDisplays.map(display => (
                  <div key={display._id} className="list-group-item">
                    <div className="row align-items-center">
                      <div className="col-md-6">
                        <h5 className="mb-1">
                          <Link to={`/my-displays/${display._id}`}>
                            {display.name}
                          </Link>
                          {display.customName && (
                            <small className="text-muted ms-2">
                              ({display.customName})
                            </small>
                          )}
                        </h5>
                        <p className="mb-1">
                          <StatusIndicator 
                            status={display.status as 'active' | 'inactive' | 'pending'} 
                            showPulse={display.status === 'active'}
                            className="me-2"
                          />
                          {display.location?.zipcode || 'No location'}
                          {display.location?.city && display.location?.state && ` (${display.location.city}, ${display.location.state})`}
                          {display.location?.label && ` - ${display.location.label}`}
                        </p>
                        {display.analytics?.lastActiveDate && (
                          <small className="text-muted">
                            Last active: {formatLastActiveTime(display.analytics.lastActiveDate)}
                          </small>
                        )}
                      </div>
                      <div className="col-md-3">
                        {display.analytics && (
                          <div className="d-flex justify-content-around">
                            <div key="notes-shown" className="text-center">
                              <small className="d-block text-muted">Notes Shown</small>
                              <strong>{display.analytics.totalNotesShown || 0}</strong>
                            </div>
                            <div key="qr-scans" className="text-center">
                              <small className="d-block text-muted">QR Scans</small>
                              <strong>{display.analytics.totalQrScans || 0}</strong>
                            </div>
                            <div key="inspired" className="text-center">
                              <small className="d-block text-muted">Inspired</small>
                              <strong>{display.analytics.notesInspired || 0}</strong>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="col-md-3 text-end">
                        <div className="btn-group btn-group-vertical-mobile d-md-flex" role="group">
                          {display.status === 'active' ? (
                            <button 
                              className="btn btn-sm btn-warning" 
                              onClick={() => handleDeactivateDisplay(display._id || '')}
                              disabled={!display._id}
                            >
                              Deactivate
                            </button>
                          ) : (
                            <button 
                              className="btn btn-sm btn-success" 
                              onClick={() => handleActivateDisplay(display._id || '')}
                              disabled={!display._id}
                            >
                              Activate
                            </button>
                          )}
                          <Link 
                            to={`/my-displays/${display._id}`} 
                            className="btn btn-sm btn-primary"
                          >
                            Edit
                          </Link>
                          <button 
                            className="btn btn-sm btn-danger" 
                            onClick={() => handleDeleteDisplay(display._id || '', display.name)}
                            disabled={!display._id}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DisplaysListPage; 